// Utility functions for formatting amounts

/**
 * Format a number with commas as thousands separators
 * @param {string|number} value - The value to format
 * @returns {string} - The formatted value with commas
 */
export const formatWithCommas = (value) => {
  // Remove existing commas and convert to string
  const cleanValue = value.toString().replace(/,/g, '');

  // Split into integer and decimal parts
  const parts = cleanValue.split('.');

  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Rejoin with decimal part if it exists
  return parts.join('.');
};

/**
 * Remove commas from a formatted number string
 * @param {string} value - The formatted value with commas
 * @returns {string} - The value without commas
 */
export const removeCommas = (value) => {
  return value.replace(/,/g, '');
};
// Display adapters only. USD config, tickets and cart storage remain untouched.
import {
  snapPresets, snapUnit, toDisplay, toUsd, formatMoney, parseAmountInput,
  toMinorUnits, fromMinorUnits, SYMBOLS,
} from '@charitystack/shared/currency';

// Presets and defaults round UP to the preset ladder (CON-744, D-R54): the org never receives
// less than the amount it set.
export function snapPresetSlots(usdPresets, ctx, bounds) {
  const rows = snapPresets(usdPresets, ctx.fxRate, ctx.displayCurrency, { ...bounds, mode: 'up' });
  return { slots: rows.map(row => row.display), sourceIndex: rows.map(row => row.sourceIndex) };
}

// A form's crowdfunding giving levels, rounded up together like presets (CON-744, D-R54,
// superseding D-R30's "never snap"): `display` is what the donor sees and `usd` what they are
// charged, never below the level's USD. Rounded as one set, so two levels at different prices that
// land on one figure are bumped apart as presets are. A level is the org's own figure, so it is never clamped down:
// when its rounded charge would pass the org's max (`maxUsd`, which checkout enforces), it keeps
// its exact conversion and its own USD instead.
export function givingLevelUnits(levelsUsd, ctx, maxUsd) {
  if (!ctx.converted) return levelsUsd.map(usd => ({ display: usd, usd }));
  // Each distinct amount is rounded once, cheapest first, so a cheaper level always takes the
  // lower figure and two levels at one price share one figure (neither is bumped).
  const amounts = levelsUsd.map(usd => Number(usd));
  const distinct = [...new Set(amounts.filter(Number.isFinite))].sort((a, b) => a - b);
  const rows = snapPresets(distinct.map(String), ctx.fxRate, ctx.displayCurrency, { mode: 'up' });
  const byAmount = new Map(distinct.map((amount, index) => [amount, rows[index]]));
  return levelsUsd.map((usd, index) => {
    const row = byAmount.get(amounts[index]);
    const overMax = row && maxUsd != null && maxUsd !== '' && Number(row.usd) > Number(maxUsd);
    if (row?.kind === 'preset' && !overMax) return { display: row.display, usd: row.usd };
    return { display: displayFixed(usd, ctx), usd: String(usd) };
  });
}

// One level's unit within its form's set (`options`, the levels shown), found by identity, then
// by title and amount; a level outside the set is rounded on its own.
export function givingLevelUnitIn(option, options, ctx, maxUsd) {
  const list = Array.isArray(options) ? options : [];
  let index = list.indexOf(option);
  if (index < 0) {
    index = list.findIndex(o => o?.title === option?.title && String(o?.amount) === String(option?.amount));
  }
  if (index < 0) return givingLevelUnits([option.amount], ctx, maxUsd)[0];
  return givingLevelUnits(list.map(o => o.amount), ctx, maxUsd)[index];
}

// A single level rounded on its own (no sibling levels to bump against).
export function givingLevelUnit(usd, ctx, maxUsd) {
  return givingLevelUnits([usd], ctx, maxUsd)[0];
}

export function displayPresets(usdPresets, ctx, bounds) {
  return ctx.converted ? snapPresetSlots(usdPresets, ctx, bounds).slots : usdPresets;
}

export function displayDefault(usdDefault, usdPresets, ctx, bounds) {
  if (!ctx.converted || usdDefault === '' || String(usdDefault) === '0') return usdDefault;
  const index = usdPresets.findIndex(value => String(value) === String(usdDefault));
  if (index >= 0) return displayPresets(usdPresets, ctx, bounds)[index];
  return displayPresets([usdDefault], ctx, bounds)[0];
}

export function displayFixed(usd, ctx) {
  return ctx.converted ? toDisplay(usd, ctx.fxRate, ctx.displayCurrency) : usd;
}

// CON-750: a total (goal, raised, a donor-list gift) — exact, never snapped. A value that is not a
// finite number is returned as it came, so a bad row renders as it did in USD instead of throwing.
export function displayTotal(usd, ctx) {
  const value = Number(usd);
  return ctx.converted && Number.isFinite(value) ? Number(displayFixed(value, ctx)) : usd;
}

// Owner ruling ui-a: the donor's symbol or code alone, no "≈"; "$" when not converted.
export function moneyPrefix(ctx) {
  return ctx.converted ? SYMBOLS[ctx.displayCurrency] || `${ctx.displayCurrency} ` : '$';
}

// CON-803: a donor-list row in the currency that donor actually paid (the row's own
// displayCurrency/displayAmount from the backend), never the visitor's. A row without them, or
// with a value that is not a currency code and a non-negative number, is USD as before.
export function donorRowMoney(row, usd) {
  const code = row?.displayCurrency;
  const amount = Number(row?.displayAmount);
  if (typeof code === 'string' && /^[A-Z]{3}$/.test(code) && code !== 'USD'
      && typeof row.displayAmount === 'string' && Number.isFinite(amount) && amount >= 0) {
    return { prefix: SYMBOLS[code] || `${code} `, amount };
  }
  return { prefix: '$', amount: usd };
}

export function fixedUnit(usd, ctx) {
  return ctx.converted ? snapUnit(usd, ctx.fxRate, ctx.displayCurrency) : { display: usd, usd };
}

export function usdOf(display, ctx) {
  if (display === '' || display == null) return 0;
  return Number(ctx.converted ? toUsd(display, ctx.fxRate, ctx.displayCurrency) : display);
}

export function boundMessageAmount(usd, ctx, rounding) {
  return ctx.converted ? toDisplay(usd, ctx.fxRate, ctx.displayCurrency, { rounding }) : usd;
}

// Owner ruling ui-a (P3 UI-2): a converted form shows the donor's figure alone, with no "≈".
export function formatAmountLabel(amount, ctx, fallback) {
  return ctx.converted ? formatMoney(amount, ctx.displayCurrency) : fallback(amount);
}

export function readCustomAmount(raw, ctx, previous) {
  const result = parseAmountInput(raw, ctx.displayCurrency, {
    locale: typeof navigator === 'undefined' ? undefined : navigator.language,
  });
  if (result.ok) return { value: result.amount };
  if (result.reason === 'empty') return { value: '' };
  if (result.reason === 'ambiguous-dot') {
    return { value: previous, hint: 'Type the amount without a thousands dot, e.g. 1000.' };
  }
  return { value: previous };
}

export function remapOnCurrencyChange({ amount, isButton, sourceIndex, pinUsd, newSlots, oldCtx, newCtx }) {
  if (pinUsd != null) return { amount: displayFixed(pinUsd, newCtx), isButton: false };
  const index = newSlots.sourceIndex.indexOf(sourceIndex);
  if (isButton && index >= 0 && newSlots.slots[index] !== '') {
    return { amount: newSlots.slots[index], isButton: true };
  }
  if (amount === '' || amount == null) return { amount: '', isButton: false };
  const usd = oldCtx.converted ? toUsd(amount, oldCtx.fxRate, oldCtx.displayCurrency) : amount;
  return { amount: displayFixed(usd, newCtx), isButton: false };
}

export function ticketsRound(config) {
  return config?.acceptsRoundedTicketPrices === true;
}

export function ticketUnitDisplay(priceUsd, ctx, rounded) {
  return rounded ? fixedUnit(priceUsd, ctx).display : displayFixed(priceUsd, ctx);
}

// `order` is the projection's purchasedGroups plus its source ticket configs.
// PER_SEAT mappings count groups, so only purchasedGroups supplies the seat count (DK-19).
export function ticketDisplayTotal(mapping, amtUsd, ctx, rounded, order = {}) {
  if (!ctx.converted || !rounded) return displayFixed(amtUsd, ctx);
  const total = mapping.reduce((sum, line) => {
    if (!line.numSold) return sum;
    const ticket = order.tickets?.find(ticket => ticket.id === line.id);
    const isGroup = ticket?.ticketType === 'GROUP' || ticket?.type === 'GROUP';
    const perSeat = isGroup && ticket.pricingMode === 'PER_SEAT';
    const quantity = perSeat
      ? (order.purchasedGroups || []).filter(group => group.ticketID === line.id)
        .reduce((seats, group) => seats + group.seatCount, 0)
      : line.numSold;
    // The legacy/flat unit is an exact cent price. Divide cents, never decimal money.
    const unit = perSeat ? ticket.price : fromMinorUnits(
      toMinorUnits(line.soldTotalPrice, 'USD') / BigInt(line.numSold), 'USD',
    );
    return sum + toMinorUnits(fixedUnit(unit, ctx).display, ctx.displayCurrency) * BigInt(quantity);
  }, 0n);
  return fromMinorUnits(total, ctx.displayCurrency);
}

export function cartLineDisplay(item, ctx) {
  if (!ctx.converted) return (item.amount || 0) * (item.quantity || 1);
  const carried = item?.__charitystackDisplay;
  if (
    carried?.displayCurrency === ctx.displayCurrency &&
    carried?.fxRate === ctx.fxRate
  ) {
    try {
      const carriedUnit =
        carried.displayUnitsByProductId?.[item?.productID] ??
        carried.displayUnitAmount;
      return fromMinorUnits(
        toMinorUnits(carriedUnit, ctx.displayCurrency) *
          BigInt(Number(item.quantity) || 1),
        ctx.displayCurrency,
      );
    } catch {
      // Older or malformed cart metadata falls back to the USD source of truth.
    }
  }
  const lineUsd = fromMinorUnits(toMinorUnits(item.amount || 0, 'USD') * BigInt(item.quantity || 1), 'USD');
  return displayFixed(lineUsd, ctx);
}

export function cartDisplayTotal(items, ctx) {
  if (!ctx.converted) return items.reduce((sum, item) => sum + cartLineDisplay(item, ctx), 0);
  return fromMinorUnits(items.reduce((sum, item) =>
    sum + toMinorUnits(cartLineDisplay(item, ctx), ctx.displayCurrency), 0n), ctx.displayCurrency);
}
